import { useInfiniteQuery, useMutation, useQuery } from "@tanstack/react-query";
import { LeadService, LeadInsert } from "@/services/leads/LeadService";
import { VisitorLeadService, type VisitorLeadInput } from "@/services/leads/VisitorLeadService";
import { Agent } from "@/types/agent/agent";

interface UseLeadsOptions {
  pageSize: number;
  selectedAgent: string | null;
  statusFilter: string;
  priorityFilter: string;
  dateRange: string;
  searchTerm: string;
  enabled?: boolean;
  agents: Agent[];
  type?: string;
}

interface UseLeadStatsOptions {
  selectedAgent: string | null;
  agents: Agent[];
  enabled?: boolean;
  type?: string;
}

export const useLeads = ({
  pageSize,
  selectedAgent,
  statusFilter,
  priorityFilter,
  dateRange,
  searchTerm,
  enabled = true,
  agents,
  type,
}: UseLeadsOptions) => {
  const agentIds = selectedAgent ? [selectedAgent] : agents.map((a) => a.id);

  const query = useInfiniteQuery({
    queryKey: [
      "leads-infinite",
      pageSize,
      selectedAgent,
      statusFilter,
      priorityFilter,
      dateRange,
      searchTerm,
      agents.length,
      type,
    ],
    queryFn: async ({ pageParam = 0 }) => {
      const result = await LeadService.getLeadsForUser({
        agentIds,
        status: statusFilter !== "all" ? statusFilter : undefined,
        priority: priorityFilter !== "all" ? priorityFilter : undefined,
        dateRange,
        searchTerm: searchTerm || undefined,
        limit: pageSize,
        offset: pageParam,
        type,
      });

      if (result.error) {
        throw new Error(result.error.message || "Failed to fetch leads");
      }

      return {
        leads: result.data || [],
        nextOffset: (result.data?.length || 0) === pageSize ? pageParam + pageSize : undefined,
        totalCount: result.totalCount || 0,
      };
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage) => lastPage.nextOffset,
    enabled,
    staleTime: 1000 * 60,
    gcTime: 1000 * 60 * 5,
  });

  const leads = query.data?.pages.flatMap((page) => page.leads) || [];
  const totalCount = query.data?.pages[0]?.totalCount || 0;

  return {
    leads,
    totalCount,
    hasNextPage: query.hasNextPage,
    fetchNextPage: query.fetchNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    isLoading: query.isLoading,
    error: query.error,
  };
};

export const useCreateLead = () =>
  useMutation({
    mutationFn: (leadData: LeadInsert) => LeadService.createLead(leadData),
  });

/** A visitor leaving their email on a genie page. Anonymous-safe; throws on failure. */
export const useCaptureVisitorLead = () =>
  useMutation({
    mutationFn: (input: VisitorLeadInput) => VisitorLeadService.capture(input),
  });

export const useLeadStats = ({ selectedAgent, agents, enabled = true, type }: UseLeadStatsOptions) => {
  const agentIds = selectedAgent ? [selectedAgent] : agents.map((a) => a.id);

  return useQuery({
    queryKey: ["lead-stats", selectedAgent, agents.length, type],
    queryFn: async () => {
      const result = await LeadService.getLeadStatsForUser(agentIds, type);
      if (result.error) {
        throw new Error(result.error.message || "Failed to fetch lead stats");
      }
      return result.data;
    },
    enabled,
    staleTime: 1000 * 60,
    gcTime: 1000 * 60 * 5,
  });
};
